import React, { useState } from 'react'
import { useVehicles } from '../contexts/VehicleContext'
import CVSVehicleList from './CVSVehicleList'
import MacStyleVehiclePanel from './MacStyleVehiclePanel'
import FleetMap from './FleetMap'
import SpeedChart from './SpeedChart'
import EventChart from './EventChart'
import Header from './Header'
import { ChevronLeft, ChevronRight, Settings, Search } from 'lucide-react'

const CVSFleetDashboard: React.FC = () => {
  const { state, selectVehicle } = useVehicles()
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false)
  const [bottomPanelCollapsed, setBottomPanelCollapsed] = useState(false)
  const [timeRange, setTimeRange] = useState(2160)
  const [activeTab, setActiveTab] = useState<'speed' | 'events'>('speed')
  const [showVehiclePanel, setShowVehiclePanel] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [startDate, setStartDate] = useState<string>(() => {
    // Default to 3 months ago to capture August data
    const date = new Date()
    date.setMonth(date.getMonth() - 3)
    return date.toISOString().split('T')[0]
  })
  const [endDate, setEndDate] = useState<string>(() => {
    return new Date().toISOString().split('T')[0]
  })

  const handleVehicleSelect = (vehicle: any) => {
    selectVehicle(vehicle)
    // Auto-expand bottom panel when vehicle is selected
    if (bottomPanelCollapsed) {
      setBottomPanelCollapsed(false)
    }
  }

  // Filter vehicles based on search term
  const filteredVehicles = state.vehicles.filter(vehicle =>
    vehicle.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vehicle.plateNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vehicle.driver?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Header */}
      <Header />
      
      <div className="flex flex-1 overflow-hidden">
        {/* Left Panel - Vehicle List */}
        <div className={`bg-white border-r border-gray-200 transition-all duration-300 ${
          leftPanelCollapsed ? 'w-0' : 'w-80'
        } overflow-hidden`}>
          {!leftPanelCollapsed && (
            <div className="h-full flex flex-col">
              {/* Search bar */}
              <div className="p-4 border-b bg-gray-50">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Chercher véhicule..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              {/* Vehicle list */}
              <div className="flex-1 overflow-hidden">
                <CVSVehicleList
                  vehicles={filteredVehicles}
                  selectedVehicle={state.selectedVehicle}
                  onVehicleSelect={handleVehicleSelect}
                  loading={state.loading}
                />
              </div>
            </div>
          )}
        </div>

        {/* Left panel toggle button */}
        <button
          onClick={() => setLeftPanelCollapsed(!leftPanelCollapsed)}
          className="w-6 bg-white border-r border-gray-200 hover:bg-gray-50 flex items-center justify-center"
        >
          {leftPanelCollapsed ? (
            <ChevronRight className="w-4 h-4 text-gray-600" />
          ) : (
            <ChevronLeft className="w-4 h-4 text-gray-600" />
          )}
        </button>

        {/* Main content area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Map container */}
          <div className={`flex-1 relative transition-all duration-300 ${
            bottomPanelCollapsed ? 'h-full' : 'h-2/3'
          }`}>
            <FleetMap
              vehicles={filteredVehicles}
              selectedVehicle={state.selectedVehicle}
              onVehicleSelect={handleVehicleSelect}
              timeRange={timeRange}
            />
            
            {/* Map controls overlay with calendar and vehicle panel toggle */}
            <div className="absolute top-4 left-4 bg-white rounded-lg shadow-lg p-3">
              <div className="flex items-center space-x-2 mb-3">
                <button
                  onClick={() => setShowVehiclePanel(!showVehiclePanel)}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    showVehiclePanel 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  🚗 Vehicles
                </button>
              </div>
              <div className="flex items-center space-x-4">
                {/* Date Range Picker */}
                <div className="flex items-center space-x-2">
                  <label className="text-xs text-gray-600">Du:</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <label className="text-xs text-gray-600">Au:</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <button className="p-2 hover:bg-gray-100 rounded">
                  <Settings className="w-4 h-4 text-gray-600" />
                </button>
              </div>
            </div>

            {/* Vehicle count overlay */}
            <div className="absolute top-4 right-4 bg-white rounded-lg shadow-lg px-3 py-2">
              <div className="text-sm">
                <span className="font-semibold">{filteredVehicles.length}</span> véhicules
                {searchTerm && (
                  <span className="text-gray-500 ml-1">
                    (filtré de {state.vehicles.length})
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Bottom panel toggle */}
          <button
            onClick={() => setBottomPanelCollapsed(!bottomPanelCollapsed)}
            className="h-6 bg-white border-t border-gray-200 hover:bg-gray-50 flex items-center justify-center"
          >
            {bottomPanelCollapsed ? (
              <ChevronRight className="w-4 h-4 text-gray-600 transform rotate-90" />
            ) : (
              <ChevronLeft className="w-4 h-4 text-gray-600 transform rotate-90" />
            )}
          </button>

          {/* Bottom Panel - Analytics with Tabs */}
          <div className={`bg-white border-t border-gray-200 transition-all duration-300 ${
            bottomPanelCollapsed ? 'h-0' : 'h-1/3'
          } overflow-hidden`}>
            {!bottomPanelCollapsed && (
              <div className="h-full flex flex-col">
                {/* Tab Navigation */}
                <div className="flex items-center space-x-1 px-4 py-2 border-b bg-gray-50">
                  <button
                    onClick={() => setActiveTab('speed')}
                    className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                      activeTab === 'speed' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                    }`}
                  >
                    📈 Speed Analysis
                  </button>
                  <button
                    onClick={() => setActiveTab('events')}
                    className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                      activeTab === 'events' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                    }`}
                  >
                    🛑 Stop & Events
                  </button>
                </div>
                
                {/* Tab Content */}
                <div className="flex-1 overflow-hidden">
                  {activeTab === 'speed' ? (
                    <SpeedChart 
                      vehicle={state.selectedVehicle} 
                      timeRange={timeRange}
                    />
                  ) : (
                    <EventChart 
                      vehicle={state.selectedVehicle} 
                      timeRange={timeRange}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Status bar */}
      <div className="bg-gray-800 text-white px-4 py-2 text-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span>🟢 {state.vehicles.filter(v => v.status === 'moving').length} en route</span>
            <span>🔴 {state.vehicles.filter(v => v.status === 'stopped').length} arrêtés</span>
            <span>🟡 {state.vehicles.filter(v => v.status === 'idle').length} inactifs</span>
            <span>⚫ {state.vehicles.filter(v => v.status === 'offline').length} hors ligne</span>
          </div>
          <div>
            Dernière mise à jour: {new Date().toLocaleTimeString('fr-FR')}
          </div>
        </div>
      </div>

      {/* Mac-style floating vehicle panel */}
      {showVehiclePanel && (
        <MacStyleVehiclePanel
          vehicles={filteredVehicles}
          selectedVehicle={state.selectedVehicle}
          onVehicleSelect={handleVehicleSelect}
          onClose={() => setShowVehiclePanel(false)}
        />
      )}
    </div>
  )
}

export default CVSFleetDashboard
